import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from "@angular/material/tooltip";
import { PollRequest } from "../user-request.types";
import { UserRequestService } from "../user-request.service";
import { ScaleQuestionComponent } from "./scale-question/scale-question.component";
import { ClosedQuestionComponent } from "./closed-question/closed-question.component";
import { AgreeDisagreeQuestionComponent } from "./agree-disagree-question/agree-disagree-question.component";
import { FiveStarsQuestionComponent } from "./five-stars-question/five-stars-question.component";
import { PollService } from "app/frontend/lib/polls/polls.service";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { debounceTime, distinctUntilChanged, filter, mergeMap, of, Subject, takeUntil, tap } from "rxjs";
import Delta from "quill-delta";
import { LinkPreview, RichTextComment } from "@teammood/timeline";
import { TmRichTextAreaComponent } from "../../../shared/ui/rich-text-area/rich-text-area.component";
import { LinkPreviewComponent } from "../../../shared/ui/linkpreview/linkpreview";
import { MoodsService } from "../../timeline-item/moods/moods.service";

@Component({
    selector: "poll-request",
    templateUrl: "./poll-request.component.html",
    styleUrls: ["./poll-request.component.scss"],
    providers: [PollService],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateModule,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatRadioModule,
        MatTooltipModule,
        ScaleQuestionComponent,
        ClosedQuestionComponent,
        AgreeDisagreeQuestionComponent,
        FiveStarsQuestionComponent,
        TmRichTextAreaComponent,
        LinkPreviewComponent
    ]
})
export class PollRequestComponent implements OnChanges {

    protected pollRequestStep: number = 1;

    protected rtaContent: Delta;
    protected linkPreviews: LinkPreview[] = [];
    protected isFieldEmplty: boolean = true;

    @Input() context: 'addOtherFeedback' | 'timeline';
    @Input() pollRequest: PollRequest;
    @Input() remainingPollNb: number;                           


    @Output() onQuestionNext = new EventEmitter<any>();

    private _destroy$ = new Subject<void>();

    constructor(
        private userRequestService: UserRequestService,
        private pollService: PollService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private moodsService: MoodsService,
        protected cdr: ChangeDetectorRef
    ) {
        this.rtaContent = new Delta();
    }
    
    ngOnChanges(changes: SimpleChanges): void {
        if(changes.pollRequest?.currentValue){
            this.pollRequest = changes.pollRequest.currentValue;
            this.pollRequestStep = 1; 
        }
    }

    // step 1
    protected postAnswer(choiceId: string){
        this.userRequestService.postTheUserAnswerToTheQuestion(this.pollRequest.poll.id, choiceId).then(response =>{
            if(response.result === 'success'){
                if (response.poll.allowComments) {
                    this.pollRequestStep = 2;
                    this.remainingPollNb--;

                } else {
                    this.onQuestionNext.emit();
                }
            }
        })
    }

    protected askMeLater() {
        this.onQuestionNext.emit();
        this.remainingPollNb--;
    }

    //step 2
    protected clearComment(){
        this.rtaContent = new Delta();
    }

    protected postComment(){
        const comment = this.rtaContent;
        if (comment && !this.isFieldEmplty) {
            this.pollService.postUserCommentToPoll(this.pollRequest.poll.id, comment).pipe(
                tap(poll => {
                    if (poll.poll.error) {
                        this.snackBarService.error(this.translateService.instant(poll.poll.error));
                    } else {
                        this.snackBarService.success(this.translateService.instant('poll.comment.success'));
                        this.clearComment();
                        this.onQuestionNext.emit();
                    }
                }),
                takeUntil(this._destroy$),
            ).subscribe();
        }
    }

    protected skipAddingComment(){
        this.onQuestionNext.emit();
    }

    protected onContentChange(update: Delta){
        of(update).pipe(
            debounceTime(500),
            distinctUntilChanged(),
            filter(update => update.ops.filter(op => op.attributes?.link).length > 0), // any url?
            mergeMap(update => {
                return this.moodsService.getDeltaLinkPreviews(update).pipe(
                    tap(previews => {
                        this.linkPreviews = previews.filter(preview => preview.title && preview.description);
                    }),
                )
            }),
            takeUntil(this._destroy$),
        ).subscribe();

        this.rtaContent = update;
        this.isFieldEmplty = RichTextComment.isEmptyDelta(this.rtaContent);
        this.cdr.detectChanges(); // Force change detection
    }

}